import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import type { MediaItem, Playlist, PlaylistDetail } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { Button, Field, Panel, TextInput, Toggle, cx } from "../control/ui/primitives";
import { IconPlus, IconTrash } from "../control/ui/icons";
import { CatalogBrowser } from "./CatalogBrowser";
import { ClipGrid } from "./ClipGrid";
import { ClipLightboxController } from "./ClipLightboxController";

/**
 * Editor's Choice curation. Left: the collections. Right: the selected collection
 * with its clips + a reviewer to browse RedGifs / Reddit / the existing catalog and
 * add clips into it. Adding a clip caches it to local disk server-side.
 */
export function CollectionsAdmin() {
  const [collections, setCollections] = useState<Playlist[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    const { collections: cs } = await adminApi.collections.list();
    setCollections(cs);
    setSelectedId((cur) => cur ?? cs[0]?.id ?? null);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(e: FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const c = await adminApi.collections.create(name);
    setNewName("");
    await load();
    setSelectedId(c.id);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Collections</h1>
          <p className="text-xs text-zinc-500">
            Curate Editor's Choice playlists. Publish one to show it on Browse and in the
            cave source picker.
          </p>
        </div>
        <form onSubmit={create} className="flex shrink-0 gap-2">
          <TextInput
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New collection…"
            className="w-48"
            maxLength={80}
          />
          <Button type="submit" variant="primary" disabled={!newName.trim()} className="shrink-0">
            <IconPlus />
            Create
          </Button>
        </form>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[15rem_1fr]">
        <aside className="space-y-1.5">
          {collections === null ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : collections.length === 0 ? (
            <p className="text-sm text-zinc-500">No collections yet.</p>
          ) : (
            collections.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedId(c.id)}
                className={cx(
                  "flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                  c.id === selectedId
                    ? "border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-zinc-100"
                    : "border-[var(--color-edge)] bg-[var(--color-panel)] text-zinc-300 hover:border-[var(--color-accent)]/50",
                )}
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate">{c.name}</span>
                  {c.public ? (
                    <span className="shrink-0 rounded bg-emerald-500/20 px-1 text-[9px] uppercase tracking-wide text-emerald-300">
                      live
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-xs text-zinc-500">{c.clipCount}</span>
              </button>
            ))
          )}
        </aside>

        <section>
          {selectedId ? (
            <CollectionDetail key={selectedId} id={selectedId} onChanged={load} />
          ) : (
            <p className="py-16 text-center text-sm text-zinc-500">
              Create a collection to start curating.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

function CollectionDetail({ id, onChanged }: { id: string; onChanged: () => Promise<void> }) {
  const [detail, setDetail] = useState<PlaylistDetail | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // The clip open in the lightbox — either from this collection's clips or the
  // reviewer below. We track which list so ‹ › navigates the right one.
  const [active, setActive] = useState<{ src: "collection" | "adder"; key: string } | null>(null);
  const [adderItems, setAdderItems] = useState<MediaItem[]>([]);

  const reload = useCallback(async () => {
    const d = await adminApi.collections.get(id);
    setDetail(d);
    setName(d.name);
    setDescription(d.description ?? "");
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const addedKeys = useMemo(
    () => new Set((detail?.items ?? []).map((c) => clipKey(c))),
    [detail],
  );

  async function patch(p: {
    name?: string;
    description?: string;
    public?: boolean;
    thumbnailClipId?: string | null;
  }) {
    await adminApi.collections.update(id, p);
    await reload();
    await onChanged();
  }

  async function addClip(clip: MediaItem) {
    await adminApi.collections.addItem(id, clip);
    await reload();
    await onChanged();
  }

  async function removeClip(cid: string) {
    await adminApi.collections.removeItem(id, cid);
    await reload();
    await onChanged();
  }

  async function remove() {
    await adminApi.collections.remove(id);
    await onChanged();
  }

  if (!detail) return <p className="text-sm text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-5">
      <Panel
        title={
          <span className="flex items-center gap-2">
            {detail.name}
            <span className="rounded bg-[var(--color-accent)]/20 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[var(--color-accent-soft)]">
              Editor's Choice
            </span>
          </span>
        }
        right={
          <Button variant="danger" size="sm" onClick={remove}>
            <IconTrash />
            Delete
          </Button>
        }
      >
        <div className="space-y-3">
          <Field label="Name">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name.trim() && name !== detail.name && patch({ name: name.trim() })}
              maxLength={80}
            />
          </Field>
          <Field label="Description" hint="optional">
            <TextInput
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => description !== (detail.description ?? "") && patch({ description })}
              placeholder="Shown on the Browse card…"
              maxLength={300}
            />
          </Field>
          <Toggle
            label={detail.public ? "Published (public)" : "Draft (hidden)"}
            checked={detail.public}
            onChange={(v) => patch({ public: v })}
          />
          {detail.public && detail.items.length === 0 ? (
            <p className="text-xs text-amber-400">
              Published but empty — it won't appear on Browse until it has at least one clip.
            </p>
          ) : null}
        </div>
      </Panel>

      <Panel title={`Clips (${detail.items.length})`}>
        <ClipGrid
          items={detail.items}
          resetKey={id}
          emptyText="Empty. Add clips from the reviewer below."
          onClipClick={(clip) => setActive({ src: "collection", key: clipKey(clip) })}
          renderAction={(clip) => {
            const isThumb = detail.thumbnailClipId === clipKey(clip);
            return (
              <>
                <button
                  type="button"
                  title={isThumb ? "Browse thumbnail (click to clear)" : "Set as Browse thumbnail"}
                  onClick={() => void patch({ thumbnailClipId: isThumb ? null : clipKey(clip) })}
                  className={cx(
                    "rounded px-2 py-1 text-[10px] font-medium",
                    isThumb
                      ? "bg-[var(--color-accent)] text-white"
                      : "bg-black/60 text-zinc-200 hover:bg-black/80",
                  )}
                >
                  {isThumb ? "★ Thumb" : "☆ Thumb"}
                </button>
                <button
                  type="button"
                  onClick={() => removeClip(clipKey(clip))}
                  className="ml-auto rounded bg-black/60 px-2 py-1 text-[10px] text-red-300 hover:bg-red-500/30"
                >
                  Remove
                </button>
              </>
            );
          }}
        />
      </Panel>

      <CatalogBrowser
        onItemsChange={setAdderItems}
        onClipClick={(clip) => setActive({ src: "adder", key: clipKey(clip) })}
        renderAction={(clip) => (
          <AddButton added={addedKeys.has(clipKey(clip))} onAdd={() => addClip(clip)} />
        )}
      />

      {active ? (
        <ClipLightboxController
          key={active.key}
          list={active.src === "collection" ? detail.items : adderItems}
          startKey={active.key}
          onClose={() => setActive(null)}
          onChanged={() => {
            void reload();
            void onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/** Per-clip "Add"/"Added ✓" action for the collection-first reviewer. */
function AddButton({ added, onAdd }: { added: boolean; onAdd: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  if (added) {
    return (
      <span className="ml-auto rounded bg-emerald-500/30 px-2 py-1 text-[10px] text-emerald-200">
        Added ✓
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await onAdd();
        } finally {
          setBusy(false);
        }
      }}
      className="ml-auto rounded bg-[var(--color-accent)]/90 px-2 py-1 text-[10px] font-medium text-white hover:bg-[var(--color-accent)] disabled:opacity-50"
    >
      {busy ? "Adding…" : "Add"}
    </button>
  );
}
